import React, { useState, useEffect, useRef, useMemo } from "react"
import styled from "styled-components"
import photosData from "../../data/content/photos.json"

// ── Hardcoded parameters (approved by Steve 2026-03-07) ─────
const CFG = {
  yoffset:  -33,   // px  — hole boundary offset (neg = more overlap into photo zone)
  spacing:  26,    // px  — gap between adjacent holes
  opacity:  0.25,
  hue:      15,    // °   — red-orange family
  size:     24,    // px  — controls glow spread radius
  blur:     0,     // px  — glow softness
  gap:      17,    // px  — photo-to-photo gap
}

// rail height (dark zone top/bottom), hole dimensions
const RAIL_H   = 36
const HOLE_H   = 22
const HOLE_W   = 16
const HOLE_BOUNDARY = RAIL_H - HOLE_H / 2   // = 25px — default center on rail edge
const HOLE_TOP    = HOLE_BOUNDARY - CFG.yoffset  // = 25 - (-27) = 52px
const HOLE_BOTTOM = HOLE_BOUNDARY - CFG.yoffset  // same for bottom

const FILM_CODES = ['SWV', 'RUP420', 'KODAK', 'TRI-X', 'HP5+', 'PORTRA', 'CINE800', 'EKTAR', 'VELVIA']
// pitch = HOLE_W(16) + spacing(26) = 42px; 168 photos ≈ 50,000px → need ~1200 holes
// Use 2000 to safely cover any content width (84,000px max)
const HOLE_COUNT = 2000

// ── Pre-compute static glow shadows ─────────────────────────
function computeGlow(i) {
  const baseOps = [CFG.opacity, CFG.opacity * 0.88, CFG.opacity * 1.12]
  const op  = Math.min(1, baseOps[i % 3])
  const h   = CFG.hue
  const s   = CFG.size
  const b   = CFG.blur
  const spread1 = Math.max(1, Math.round(s * 0.04))
  const blur1   = Math.round(s * 0.08)
  const spread2 = Math.max(1, Math.round(s * 0.15))
  const blur2   = Math.round(s * 0.5 + b)
  return (
    `0 0 ${blur1}px ${spread1}px hsla(${h},90%,55%,${op.toFixed(2)}), ` +
    `0 0 ${blur2}px ${spread2}px hsla(${h + 6},75%,38%,${(op * 0.38).toFixed(2)})`
  )
}

const HOLE_GLOWS = Array.from({ length: HOLE_COUNT }, (_, i) => computeGlow(i))

// ── Styled components ────────────────────────────────────────

const SectionWrapper = styled.section`
  width: 100%;
  background: #0d0d0d;
  padding: 48px 0 40px;
  position: relative;
  overflow: hidden;

  /* Ambient leak — very subtle top/bottom */
  &::before, &::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 80px;
    pointer-events: none;
    z-index: 1;
  }
  &::before {
    top: 0;
    background: linear-gradient(to bottom,
      rgba(220,50,15,0.07) 0%, rgba(255,100,0,0.03) 60%, transparent 100%);
  }
  &::after {
    bottom: 0;
    background: linear-gradient(to top,
      rgba(220,50,15,0.07) 0%, rgba(255,100,0,0.03) 60%, transparent 100%);
  }
`

const SectionTitle = styled.h2`
  color: rgba(255,255,255,0.75);
  font-size: 18px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 28px;
  font-family: 'Courier New', Courier, monospace;
  font-weight: 300;
  position: relative;
  z-index: 2;
`

// The film strip: overflow hidden, fixed height
const StripOuter = styled.div`
  position: relative;
  width: 100%;
  height: 420px;
  overflow: hidden;
  user-select: none;
`

// Dark rail stripes (non-scrolling overlay)
const Rail = styled.div`
  position: absolute;
  left: 0; right: 0;
  height: ${RAIL_H}px;
  background: #080808;
  z-index: 2;
  pointer-events: none;
  ${({ pos }) => pos}: 0;
`

// Scrollable track — contains holes + photos, all scroll together
const ScrollTrack = styled.div`
  position: absolute;
  inset: 0;
  overflow-x: scroll;
  overflow-y: hidden;
  scrollbar-width: none;
  cursor: grab;
  &::-webkit-scrollbar { display: none; }
  &.dragging { cursor: grabbing; }
`

// Inner container: width grows with content
const ScrollInner = styled.div`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  height: 100%;
  min-width: 100%;
`

// Sprocket holes row — absolute inside ScrollInner, scrolls with it
const HolesRow = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${CFG.spacing}px;
  padding: 0 ${Math.round(CFG.spacing / 2)}px;
  pointer-events: none;
  z-index: 10;
  ${({ pos }) => pos}: ${HOLE_TOP}px;
`

const Hole = styled.div`
  width: ${HOLE_W}px;
  height: ${HOLE_H}px;
  border-radius: 4px;
  background: #181818;
  border: 1px solid #2e2e2e;
  flex-shrink: 0;
  box-shadow: ${({ glowIndex }) => HOLE_GLOWS[glowIndex % HOLE_COUNT]};
`

// Photos flex row — lives inside ScrollInner
const PhotosRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  gap: ${CFG.gap}px;
  padding: ${RAIL_H}px 60px;
  position: relative;
  z-index: 5;
`

const FilmCode = styled.span`
  flex-shrink: 0;
  align-self: center;
  writing-mode: vertical-rl;
  color: #b8860b;
  font-size: 9px;
  letter-spacing: 0.22em;
  opacity: 0.5;
  padding: 0 4px;
  font-family: 'Courier New', Courier, monospace;
`

const PhotoCard = styled.div`
  position: relative;
  height: 290px;
  flex-shrink: 0;
  cursor: pointer;
`

const PhotoImg = styled.img`
  height: 100%;
  width: auto;
  display: block;
  object-fit: cover;
  filter: contrast(1.06) saturate(0.88);
  box-shadow: 0 4px 20px rgba(0,0,0,0.8);
`

const FrameLabel = styled.span`
  position: absolute;
  bottom: -16px;
  left: 0; right: 0;
  text-align: center;
  font-size: 9px;
  letter-spacing: 0.18em;
  color: #c49800;
  opacity: 0.6;
  pointer-events: none;
  font-family: 'Courier New', Courier, monospace;
`

// ── Modal ────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0,0,0,0.85);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? "all" : "none")};
  transition: opacity 0.3s ease;
`

const ModalBox = styled.div`
  background: #141414;
  border-radius: 14px;
  overflow: hidden;
  width: 100%;
  max-width: 440px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 40px 100px rgba(0,0,0,0.9);
  border: 1px solid rgba(255,255,255,0.07);
  transform: ${({ open }) => (open ? "none" : "translateY(28px) scale(0.95)")};
  transition: transform 0.36s cubic-bezier(0.16,1,0.3,1);
`

const ModalImgWrap = styled.div`
  position: relative;
  background: #000;
  flex-shrink: 0;
`

const ModalImg = styled.img`
  width: 100%;
  max-height: 460px;
  object-fit: contain;
  display: block;
`

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 18px 20px 24px;
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: #333 transparent;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`

const ModalUser = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const AvatarRing = styled.div`
  width: 34px; height: 34px;
  border-radius: 50%; padding: 2px;
  background: linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888);
  flex-shrink: 0;
`

const AvatarInner = styled.img`
  width: 100%; height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`

const ModalUsername = styled.div`
  color: #e8e8e8; font-size: 13px; font-weight: 600;
  font-family: -apple-system, sans-serif;
`

const ModalDate = styled.div`
  color: #555; font-size: 11px;
  font-family: -apple-system, sans-serif; margin-top: 1px;
`

const IgLink = styled.a`
  color: #5b9bd5; font-size: 12px;
  font-family: -apple-system, sans-serif;
  text-decoration: none;
  display: flex; align-items: center; gap: 4px;
  opacity: 0.8;
  &:hover { opacity: 1; }
`

const Caption = styled.p`
  color: #bbb; font-size: 13px; line-height: 1.65;
  font-family: -apple-system, sans-serif;
  white-space: pre-wrap; word-break: break-word;
`

const CloseBtn = styled.button`
  position: fixed; top: 20px; right: 20px;
  width: 38px; height: 38px; border-radius: 50%;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.15);
  color: #fff; font-size: 20px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(8px);
  transition: background 0.2s; z-index: 1010;
  &:hover { background: rgba(255,255,255,0.22); }
`

// ── Component ────────────────────────────────────────────────

const FilmGallery = () => {
  const photos = photosData.photos || []

  // No rotation — all photos upright
  const rotations = useMemo(() => photos.map(() => 0), [photos])

  const filmCodeSeq = useMemo(
    () => photos.map((_, i) => (i % 6 === 0 ? FILM_CODES[Math.floor(Math.random() * FILM_CODES.length)] : null)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // ── Modal state ──────────────────────────────────────────
  const [modal, setModal] = useState(null) // { photo, open }

  const openModal = photo => setModal({ photo, open: true })
  const closeModal = () => setModal(m => m && { ...m, open: false })

  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") closeModal()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  // ── Momentum drag scroll ─────────────────────────────────
  const trackRef  = useRef(null)
  const dragState = useRef({
    dragging: false, startX: 0, startScroll: 0,
    velocity: 0, lastX: 0, lastT: 0, rafId: null,
  })

  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    const ds = dragState.current

    const cancelMomentum = () => {
      if (ds.rafId) { cancelAnimationFrame(ds.rafId); ds.rafId = null }
    }

    const momentum = () => {
      if (Math.abs(ds.velocity) < 0.5) { ds.velocity = 0; return }
      track.scrollLeft += ds.velocity
      ds.velocity *= 0.93
      ds.rafId = requestAnimationFrame(momentum)
    }

    const onMouseDown = e => {
      cancelMomentum()
      ds.dragging = true; ds.startX = ds.lastX = e.clientX
      ds.startScroll = track.scrollLeft; ds.lastT = performance.now(); ds.velocity = 0
      track.classList.add("dragging")
    }

    const onMouseMove = e => {
      if (!ds.dragging) return
      const now = performance.now(), dt = now - ds.lastT || 1
      ds.velocity = -((e.clientX - ds.lastX) / dt) * 16
      ds.lastX = e.clientX; ds.lastT = now
      track.scrollLeft = ds.startScroll - (e.clientX - ds.startX)
    }

    const onMouseUp = () => {
      if (!ds.dragging) return
      ds.dragging = false; track.classList.remove("dragging")
      ds.rafId = requestAnimationFrame(momentum)
    }

    // Touch
    let tSX, tSS, tLX, tLT, tVel = 0
    const onTouchStart = e => {
      cancelMomentum()
      tSX = tLX = e.touches[0].clientX; tSS = track.scrollLeft
      tLT = performance.now(); tVel = 0
    }
    const onTouchMove = e => {
      const now = performance.now(), x = e.touches[0].clientX
      tVel = -((x - tLX) / (now - tLT || 1)) * 16
      tLX = x; tLT = now; track.scrollLeft = tSS - (x - tSX)
    }
    const onTouchEnd = () => {
      ds.velocity = tVel; ds.rafId = requestAnimationFrame(momentum)
    }

    track.addEventListener("mousedown", onMouseDown)
    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", onMouseUp)
    track.addEventListener("touchstart", onTouchStart, { passive: true })
    track.addEventListener("touchmove", onTouchMove, { passive: true })
    track.addEventListener("touchend", onTouchEnd)

    return () => {
      cancelMomentum()
      track.removeEventListener("mousedown", onMouseDown)
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", onMouseUp)
      track.removeEventListener("touchstart", onTouchStart)
      track.removeEventListener("touchmove", onTouchMove)
      track.removeEventListener("touchend", onTouchEnd)
    }
  }, [])

  // ── Image src ─────────────────────────────────────────────
  // filename is just the basename (e.g. "DTn0XNiEnHq.jpg")
  // static/photos/ is served at /photos/ by Gatsby
  const imgSrc = photo => {
    if (photo.filename) return `/photos/${photo.filename}`
    if (photo.shortcode) return `/photos/${photo.shortcode}.jpg`
    return photo.cdnUrl || ""
  }

  // timestamp may be ISO string ("2026-01-17T18:39:07Z") or Unix int
  const formatDate = ts => {
    if (!ts) return ""
    const d = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts)
    return isNaN(d) ? "" : d.toLocaleDateString("en-US", { year: "numeric", month: "long" })
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <>
      <SectionWrapper>
        <SectionTitle>Film Photography</SectionTitle>

        <StripOuter>
          {/* Non-scrolling rail overlays (purely decorative bg) */}
          <Rail pos="top" />
          <Rail pos="bottom" />

          {/* Scroll track — holds holes + photos together */}
          <ScrollTrack ref={trackRef}>
            <ScrollInner>
              {/* Top sprocket row */}
              <HolesRow pos="top">
                {HOLE_GLOWS.map((_, i) => (
                  <Hole key={`th-${i}`} glowIndex={i} />
                ))}
              </HolesRow>

              {/* Bottom sprocket row */}
              <HolesRow pos="bottom">
                {HOLE_GLOWS.map((_, i) => (
                  <Hole key={`bh-${i}`} glowIndex={i} />
                ))}
              </HolesRow>

              {/* Photos */}
              <PhotosRow>
                {photos.map((photo, idx) => (
                  <React.Fragment key={photo.shortcode || idx}>
  
                    <PhotoCard onClick={() => openModal(photo)}>
                      <PhotoImg
                        src={imgSrc(photo)}
                        alt={photo.caption || `Film photo ${idx + 1}`}
                        loading="lazy"
                        onError={e => {
                          if (photo.cdnUrl && e.target.src !== photo.cdnUrl) {
                            e.target.src = photo.cdnUrl
                          }
                        }}
                      />
                      <FrameLabel>{String(idx + 1).padStart(2, "0")}▲</FrameLabel>
                    </PhotoCard>
                  </React.Fragment>
                ))}
              </PhotosRow>
            </ScrollInner>
          </ScrollTrack>
        </StripOuter>
      </SectionWrapper>

      {/* Modal */}
      {modal && (
        <Overlay open={modal.open} onClick={e => { if (e.target === e.currentTarget) closeModal() }}>
          <ModalBox open={modal.open} onClick={e => e.stopPropagation()}>
            <ModalImgWrap>
              <ModalImg
                src={imgSrc(modal.photo)}
                alt={modal.photo.caption || "Film photo"}
                onError={e => {
                  if (modal.photo.cdnUrl && e.target.src !== modal.photo.cdnUrl) {
                    e.target.src = modal.photo.cdnUrl
                  }
                }}
              />
            </ModalImgWrap>
            <ModalBody>
              <ModalHeader>
                <ModalUser>
                  <AvatarRing>
                    <AvatarInner src="/images/instagram-avatar.jpg" alt="steve.wong.film" />
                  </AvatarRing>
                  <div>
                    <ModalUsername>Tianyu Wang</ModalUsername>
                    <ModalDate>{formatDate(modal.photo.timestamp)}</ModalDate>
                  </div>
                </ModalUser>
                {modal.photo.shortcode && (
                  <IgLink
                    href={`https://www.instagram.com/p/${modal.photo.shortcode}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                  >
                    ↗ Instagram
                  </IgLink>
                )}
              </ModalHeader>
              {modal.photo.caption && (
                <Caption>{modal.photo.caption}</Caption>
              )}
            </ModalBody>
          </ModalBox>
          <CloseBtn onClick={e => { e.stopPropagation(); closeModal() }}>×</CloseBtn>
        </Overlay>
      )}
    </>
  )
}

export default FilmGallery
